import React, { memo, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme, Text, Icon, Button } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../lib/Web5Context";
import ConditionsContext from "./ConditionsContext";
import { getEmptyGroup, getConditionsString, validateConditions } from "./utils.js";
import Group from "./Group";
import OperatorSwitch from "./OperatorSwitch";
import styles from "./styles/root";
const useStyles = createUseStyles(styles, 'Conditions');
const Conditions = _ref => {
  let {
    conditions,
    fields,
    isReadOnly,
    onChange,
    listMapFields = {},
    options
  } = _ref;
  const {
    t,
    locale
  } = useWeb5Context();
  const classes = useStyles();
  const theme = useTheme();
  const [indexCurrentGroup, setIndexCurrentGroup] = useState(0);
  const conditionStr = useMemo(() => getConditionsString(conditions, locale, listMapFields), [conditions, locale, listMapFields]);
  const validConditions = useMemo(() => validateConditions(conditions), [conditions]);
  const handleGroupChange = useCallback((index, group) => {
    const newGroups = [...conditions.groups];
    newGroups[index] = group;
    onChange({
      operator: conditions.operator,
      groups: newGroups
    });
  }, [conditions.groups, conditions.operator, onChange]);
  const handleGroupDelete = useCallback(index => {
    const newGroups = [...conditions.groups];
    newGroups.splice(index, 1);
    onChange({
      operator: conditions.operator,
      groups: newGroups
    });
  }, [conditions.groups, conditions.operator, onChange]);
  const handleAddGroup = useCallback(() => {
    const newGroups = [...conditions.groups, getEmptyGroup()];
    setIndexCurrentGroup(newGroups.length - 1);
    onChange({
      operator: conditions.operator,
      groups: newGroups
    });
  }, [conditions.groups, conditions.operator, onChange]);
  const handleLogicOperatorChange = useCallback(val => {
    onChange({
      operator: val,
      groups: conditions.groups
    });
  }, [conditions.groups, onChange]);
  const renderGroup = useCallback((group, index) => {
    var _conditions$groups;
    return /*#__PURE__*/React.createElement(Group, {
      key: `group-${index}`,
      group: group,
      index: index,
      onChange: !isReadOnly ? handleGroupChange : undefined,
      onDelete: !isReadOnly && (conditions === null || conditions === void 0 ? void 0 : (_conditions$groups = conditions.groups) === null || _conditions$groups === void 0 ? void 0 : _conditions$groups.length) > 1 ? handleGroupDelete : undefined,
      expanded: index === indexCurrentGroup,
      toggleExpanded: setIndexCurrentGroup,
      listMapFields: listMapFields,
      options: options
    });
  }, [listMapFields, isReadOnly, handleGroupChange, conditions, handleGroupDelete, indexCurrentGroup, options]);
  const renderGroupSeparator = useCallback((prev, current, index) => [prev, /*#__PURE__*/React.createElement("div", {
    key: `separator-${index}`,
    className: classes.groupSeparator
  }, index === 1 && /*#__PURE__*/React.createElement(OperatorSwitch, {
    value: conditions.operator,
    onChange: !isReadOnly ? handleLogicOperatorChange : undefined
  })), current], [classes.groupSeparator, conditions.operator, handleLogicOperatorChange, isReadOnly]);
  const renderedGroups = useMemo(() => conditions.groups && conditions.groups.map(renderGroup).reduce(renderGroupSeparator), [conditions.groups, renderGroup, renderGroupSeparator]);
  const context = useMemo(() => ({
    fields,
    isReadOnly
  }), [fields, isReadOnly]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(ConditionsContext.Provider, {
    value: context
  }, renderedGroups, !isReadOnly && /*#__PURE__*/React.createElement("div", {
    className: classes.action
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: handleAddGroup,
    type: "terciary",
    className: "conditions__add-condition-group-button"
  }, t('cfm_action_add_group'))), conditionStr && conditionStr.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.conditionStringContainer
  }, validConditions ? /*#__PURE__*/React.createElement(Icon, {
    name: "thickEnabledFilled",
    color: theme.colors.semantic.positive500
  }) : /*#__PURE__*/React.createElement(Icon, {
    name: "thickDisabled",
    color: theme.colors.semantic.negative500
  }), /*#__PURE__*/React.createElement(Text, {
    className: classes.conditionString
  }, conditionStr))));
};
process.env.NODE_ENV !== "production" ? Conditions.propTypes = {
  conditions: PropTypes.shape({}).isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    type: PropTypes.oneOf(['bit', 'text', 'datetime', 'decimal', 'int', 'list', 'multivalue', 'currency', 'percentage']).isRequired,
    table: PropTypes.string,
    conditions: PropTypes.arrayOf(PropTypes.string)
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  listMapFields: PropTypes.shape({})
} : void 0;
export { getEmptyConditions, mapToJsonLogic, mapConditions, getConditionsString, mapFieldsValues, validateConditions, transformDataConditions } from "./utils";
export default /*#__PURE__*/memo(Conditions);